import { useEffect, useRef, useState } from "react";
import { OrderHistoryService, OrderViewData } from "../services/order/orderHistoryService";
import { StringsService } from "../services/strings/strings";
import { onMessage } from "firebase/messaging";
import { messaging } from "../firebase";
import { FullMenuPageData } from "../profilePage/maintainSpecificRestaurant/upsertFullMenuHelpers";
import { menuService } from "../services/firebase/menuService";


export function useRestaurantOrderHistory(restaurantId: string | undefined) {
  const [pendingOrders, setPendingOrders] = useState<OrderViewData[]>([]);
  const [ordersPendingAcceptance, setOrdersPendingAcceptance] = useState<OrderViewData[]>([]);
  const [collectedOrders, setCollectedOrders] = useState<OrderViewData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const previousPendingIdsRef = useRef<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [menuData, setMenuData] = useState<FullMenuPageData>({ baseItems: [], menu: { categories: [], bundles: [] } });

  const loadOrdersRef = useRef<() => void>(() => { });
  const isFirstLoadRef = useRef(true);
  const refreshOrdersRef = useRef<() => void>(() => {
    console.log("refreshOrders function not set yet");
  });

  useEffect(() => {
    if (!restaurantId) {
      setPendingOrders([]);
      setOrdersPendingAcceptance([]);
      setCollectedOrders([]);
      setLoading(false);
      return;
    }

    const loadOrders = async () => {
      // inside loadOrders
      const playNotificationSound = () => {
        if (!audioRef.current) {
          audioRef.current = new Audio("/sounds/ping.mp3");
        }
        audioRef.current.play().catch((err) => {
          console.warn("Audio play failed (possibly due to user interaction restrictions):", err);
        });
      };

      try {
        setLoading(true);

        const menu = await menuService.fetchFullMenuCached(restaurantId);
        setMenuData(menu);

        const { collectedOrders, pendingOrders } = await OrderHistoryService.getAllOrdersByRestaurantId(restaurantId);

        const newOrderIds = new Set(pendingOrders.map(order => order.cart.id));
        const previousOrderIds = previousPendingIdsRef.current;
        const hasNewOrders = pendingOrders.some(order => !previousOrderIds.has(order.cart.id));
        const hasAnyOrders = newOrderIds.size > 0;

        const isFirstLoad = isFirstLoadRef.current;
        if ((isFirstLoad && hasAnyOrders) || (!isFirstLoad && hasNewOrders)) {
          playNotificationSound();
        }

        // Update reference for future comparisons
        previousPendingIdsRef.current = newOrderIds;
        isFirstLoadRef.current = false;

        setCollectedOrders(collectedOrders);
        setPendingOrders(pendingOrders);
        setOrdersPendingAcceptance(pendingOrders.filter(order => order.cart.status == "PAYMENT_COMPLETE"))

        setError(null);
      } catch (error) {
        console.error("Error fetching restaurant orders:", error);
        setPendingOrders([]);
        setCollectedOrders([]);
        setError("Failed to fetch restaurant orders");
      } finally {
        setLoading(false);
      }
    };

    loadOrdersRef.current = loadOrders;
    refreshOrdersRef.current = () => {
      console.log("refreshing orders");
      loadOrdersRef.current();
    };

    loadOrdersRef.current(); // initial fetch
    const intervalId = setInterval(() => loadOrdersRef.current(), 10 * 60 * 1000); // 10 minutes

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("Push message received", payload);
      loadOrdersRef.current();
    });

    return () => {
      clearInterval(intervalId);
      unsubscribe(); // cleanup listener
    };
  }, [restaurantId]);

  return {
    pendingOrders,
    ordersPendingAcceptance,
    collectedOrders,
    loading,
    error,
    menuData,
    refreshOrders: refreshOrdersRef.current,
  };
}
